import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Listing, CrowdVoltListing, CrowdVoltEvent } from '../../types';
import { Button } from '../../components/base-components';
import { EditListingModal } from '../../components/EditListingModal';
import { EditCVListingModal } from '../../components/EditCVListingModal';
import { ConnectCrowdVoltModal } from '../../components/ConnectCrowdVoltModal';
import { LinkListingModal } from '../../components/LinkListingModal';
import { trackEvent } from '../../services/analytics';
import {
  useCrowdVoltStatus,
  useCrowdVoltEvents,
  useListings,
  useCrowdVoltListings,
  useDisconnectCrowdVolt,
  useUnlinkCrowdVoltListing,
  queryKeys,
} from '../../hooks/useApi';
import { useCanSeeCrowdVoltConnect } from '../../access/useFeatureAccess';
import { useToast } from '../../contexts/ToastContext';
import {
  Badge,
  DataList,
  DataListRow,
  EmptyState,
  PageHeader,
  PageShell,
  Panel,
  Skeleton,
  cx,
  type BadgeTone,
  type DataListColumn,
} from '../components';
import { ChevronDownIcon, TagIcon } from '../components/icons';
import { FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatTimeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/** v1's `statusBadge` colour map, expressed as a `Badge` tone. Same four cases. */
const statusTone = (status: string): BadgeTone => {
  switch (status) {
    case 'ACTIVE': return 'green';
    case 'INACTIVE': return 'neutral';
    case 'SOLD': return 'blue';
    default: return 'neutral';
  }
};

/** v1's `eventLinkBadge` — identical branch order, identical labels. */
const eventLinkBadge = (ev: CrowdVoltEvent, cvConnected: boolean): { label: string; tone: BadgeTone } => {
  if (!cvConnected) return { label: 'FT only', tone: 'neutral' };
  if (ev.on_both && ev.fully_linked) return { label: '⚡ Fully Linked', tone: 'green' };
  if (ev.on_both && !ev.fully_linked) return { label: '◑ Partial', tone: 'amber' };
  if (ev.cv_listing_count === 0) return { label: 'FT only', tone: 'neutral' };
  if (ev.ft_active_listing_count === 0) return { label: 'CV only', tone: 'neutral' };
  return { label: 'Partial', tone: 'amber' };
};

/** Buttons stretch to fill their row below `lg`; `Button` takes no className. */
const ACTION_ROW = 'flex gap-2 [&_button]:w-full lg:[&_button]:w-auto';

// ── The CrowdVolt identity chip (DESIGN_PLAN §5.2 `ConnectionChip`) ───────────

const ConnectionChip: React.FC<{ name: string; contact?: string }> = ({ name, contact }) => (
  <div className="flex min-w-0 items-center gap-2.5 rounded-xl border border-violet-500/20 bg-violet-500/[0.06] px-3 py-2">
    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-violet-500/20">
      <span className="text-xs font-bold text-violet-400">CV</span>
    </div>
    <div className="min-w-0">
      <p className="truncate text-xs font-medium text-white">{name}</p>
      {contact && <p className="truncate text-xs text-white/40">{contact}</p>}
    </div>
  </div>
);

// ── The CrowdVolt row used by the empty-state and orphan lists ────────────────

/**
 * Shared shell for the two "flat" CV lists. `forceUnlinked` reproduces the
 * orphan list's v1 behaviour exactly: it hardcodes the `Unlinked` badge and
 * always offers `Link to FT`, where the empty-state list branches on
 * `ft_listing_id`.
 */
const CvSummaryRow: React.FC<{
  cvl: CrowdVoltListing;
  forceUnlinked?: boolean;
  onEditPrice: () => void;
  onLink: () => void;
}> = ({ cvl, forceUnlinked, onEditPrice, onLink }) => {
  const showLink = forceUnlinked || !cvl.ft_listing_id;

  return (
    <div className="rounded-xl bg-white/[0.02] p-3 lg:flex lg:items-center lg:justify-between lg:gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-white">{cvl.event_name}</span>
          <Badge tone="neutral" size="sm">{cvl.wallet}</Badge>
          {!forceUnlinked && cvl.ft_listing_id ? (
            <Badge tone="violet" size="sm">⚡ Linked</Badge>
          ) : (
            <Badge tone="amber" size="sm">Unlinked</Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-white/40">
          ${cvl.price.toFixed(2)} × {cvl.qty} · {cvl.ticket_type}
        </p>
      </div>
      <div className={cx(ACTION_ROW, 'mt-3 lg:mt-0 lg:flex-shrink-0')}>
        <Button variant="secondary" size="sm" onClick={onEditPrice}>
          Edit Price
        </Button>
        {showLink && (
          <Button variant="secondary" size="sm" onClick={onLink}>
            Link to FT
          </Button>
        )}
      </div>
    </div>
  );
};

// ── Expanded event row ────────────────────────────────────────────────────────

const FT_COLUMNS: DataListColumn[] = [
  { key: 'price', label: 'Price', span: 4 },
  { key: 'qty', label: 'Qty', span: 1, align: 'right' },
  { key: 'status', label: 'Status', span: 2 },
  { key: 'cv', label: 'CrowdVolt', span: 3 },
  { key: 'actions', label: '', span: 2, align: 'right' },
];

const CV_COLUMNS: DataListColumn[] = [
  { key: 'price', label: 'Price', span: 3 },
  { key: 'qty', label: 'Qty', span: 1, align: 'right' },
  { key: 'wallet', label: 'Wallet', span: 2 },
  { key: 'link', label: 'Link', span: 3 },
  { key: 'actions', label: '', span: 3, align: 'right' },
];

interface EventDetailProps {
  event: CrowdVoltEvent;
  cvConnected: boolean;
  allCvListings: CrowdVoltListing[];
  onEditFT: (listing: Listing) => void;
  onEditCV: (cvListing: CrowdVoltListing, ftPrice: string | null) => void;
  onLink: (cvListing: CrowdVoltListing) => void;
  onUnlink: (ask_uqid: string) => void;
  unlinkPending: boolean;
}

const EventDetail: React.FC<EventDetailProps> = ({
  event, cvConnected, allCvListings, onEditFT, onEditCV, onLink, onUnlink, unlinkPending,
}) => {
  const { data: ftData, isLoading: ftLoading } = useListings({ event_id: event.ft_event_id });

  const ftListings: Listing[] = ftData?.data ?? [];
  // Match CV listings to this event: first by ExternalEventLink (ft_event_id), then by name
  const cvListings: CrowdVoltListing[] = allCvListings.filter(
    (l) =>
      l.ft_event_id === event.ft_event_id ||
      l.event_name?.toLowerCase() === event.ft_event_name?.toLowerCase(),
  );

  return (
    <div className="mt-4 space-y-5 border-t border-white/[0.06] pt-4">
      {/* FT Listings section */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">FT Listings</p>
        {ftLoading && <Skeleton className="h-12 w-full rounded-lg" />}
        {!ftLoading && ftListings.length === 0 && (
          <p className="py-2 text-xs text-white/30">No FT listings for this event</p>
        )}
        {!ftLoading && ftListings.length > 0 && (
          <DataList columns={FT_COLUMNS}>
            {ftListings.map((l) => {
              const price = `$${parseFloat(l.price_per_ticket).toFixed(2)}/ticket`;
              const section = l.section
                ? `Sec ${l.section}${l.row ? ` · Row ${l.row}` : ''}`
                : null;
              const cvBadge = l.cv_listing_id ? (
                <Badge tone="violet" size="sm">
                  ⚡ CV linked
                  {l.cv_last_mirrored_at && ` · ${formatTimeAgo(l.cv_last_mirrored_at)}`}
                </Badge>
              ) : null;
              const editButton = (
                <Button variant="secondary" size="sm" onClick={() => onEditFT(l)}>
                  Edit Price
                </Button>
              );

              return (
                <DataListRow
                  key={l.id}
                  cells={[
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-white">{price}</span>
                      {section && <p className="mt-0.5 truncate text-xs text-white/30">{section}</p>}
                    </div>,
                    <span className="text-xs text-white/40">× {l.quantity}</span>,
                    <Badge tone={statusTone(l.status)} size="sm">{l.status}</Badge>,
                    cvBadge ?? <span className="text-xs text-white/30">—</span>,
                    <div className="flex justify-end">{editButton}</div>,
                  ]}
                  stacked={
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-white">{price}</span>
                        <span className="text-xs text-white/40">× {l.quantity}</span>
                        <Badge tone={statusTone(l.status)} size="sm">{l.status}</Badge>
                        {cvBadge}
                      </div>
                      {section && <p className="text-xs text-white/30">{section}</p>}
                      <div className={ACTION_ROW}>{editButton}</div>
                    </div>
                  }
                />
              );
            })}
          </DataList>
        )}
      </div>

      {/* CV Listings section */}
      {cvConnected && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">CrowdVolt Listings</p>
          {cvListings.length === 0 && (
            <p className="py-2 text-xs text-white/30">No CV listings for this event</p>
          )}
          {cvListings.length > 0 && (
            <DataList columns={CV_COLUMNS}>
              {cvListings.map((cvl) => {
                const linkedFT = ftListings.find((l) => l.id === cvl.ft_listing_id);
                const isLinked = cvl.ft_listing_id !== null;

                const linkBadge = isLinked && cvl.mirror_enabled ? (
                  <Badge tone="green" size="sm">Mirroring → FT #{cvl.ft_listing_id}</Badge>
                ) : isLinked && !cvl.mirror_enabled ? (
                  <Badge tone="neutral" size="sm">Linked → FT #{cvl.ft_listing_id}</Badge>
                ) : null;

                const synced = cvl.last_mirrored_at
                  ? `synced ${formatTimeAgo(cvl.last_mirrored_at)} at $${cvl.last_mirrored_price}`
                  : null;

                const actions = (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEditCV(cvl, linkedFT?.price_per_ticket ?? null)}
                    >
                      Edit CV Price
                    </Button>
                    {isLinked ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onUnlink(cvl.ask_uqid)}
                        disabled={unlinkPending}
                      >
                        Unlink
                      </Button>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => onLink(cvl)}>
                        Link
                      </Button>
                    )}
                  </>
                );

                return (
                  <DataListRow
                    key={cvl.ask_uqid}
                    cells={[
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-white">${cvl.price.toFixed(2)}</span>
                        {synced && <p className="mt-0.5 truncate text-xs text-white/30">{synced}</p>}
                      </div>,
                      <span className="text-xs text-white/40">× {cvl.qty}</span>,
                      <Badge tone="neutral" size="sm">{cvl.wallet}</Badge>,
                      linkBadge ?? <span className="text-xs text-white/30">—</span>,
                      <div className="flex justify-end gap-2">{actions}</div>,
                    ]}
                    stacked={
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-white">${cvl.price.toFixed(2)}</span>
                          <span className="text-xs text-white/40">× {cvl.qty}</span>
                          <Badge tone="neutral" size="sm">{cvl.wallet}</Badge>
                          {linkBadge}
                        </div>
                        {synced && <p className="text-xs text-white/30">{synced}</p>}
                        <div className={ACTION_ROW}>{actions}</div>
                      </div>
                    }
                  />
                );
              })}
            </DataList>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main screen ───────────────────────────────────────────────────────────────

export const ListingsScreen: React.FC = () => {
  const { showSuccess, showError } = useToast();

  // Modal state
  const [editFTListing, setEditFTListing] = useState<Listing | null>(null);
  const [editFTOpen, setEditFTOpen] = useState(false);
  const [editCVListing, setEditCVListing] = useState<CrowdVoltListing | null>(null);
  const [editCVFTPrice, setEditCVFTPrice] = useState<string | null>(null);
  const [editCVOpen, setEditCVOpen] = useState(false);
  const [connectCVOpen, setConnectCVOpen] = useState(false);
  const [linkCVListing, setLinkCVListing] = useState<CrowdVoltListing | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);

  useEffect(() => { trackEvent('listings_page_viewed'); }, []);

  const queryClient = useQueryClient();

  // Data
  const { data: cvStatus } = useCrowdVoltStatus();
  const isConnected = cvStatus?.connected === true && !cvStatus?.is_token_expired;
  const isTokenExpired = cvStatus?.connected === true && cvStatus?.is_token_expired === true;
  const canSeeCvConnect = useCanSeeCrowdVoltConnect();

  const { data: eventsData, isLoading: eventsLoading } = useCrowdVoltEvents({ include_unlinked: true });
  const events: CrowdVoltEvent[] = eventsData?.results ?? [];
  const { data: allCvData, isLoading: allCvLoading, error: allCvError } = useCrowdVoltListings(
    { status: 'ACTIVE', sync: true},
    isConnected,
  );
  const allCvListings: CrowdVoltListing[] = allCvData?.results ?? [];
  // CV listings that don't match any event in the events list (by id or name)
  const orphanCvListings = allCvListings.filter(
    (cvl) =>
      cvl.ft_event_id === null &&
      !events.some(
        (ev) => ev.ft_event_name?.toLowerCase() === cvl.event_name?.toLowerCase(),
      ),
  );

  // After the CV sync completes, refresh the events query so cv_counts reflect
  // the newly synced ExternalMarketplaceListing records.
  useEffect(() => {
    if (allCvData) {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltEvents });
    }
  }, [allCvData]);

  // Detect a real CV session expiry: sync failed with 401 even though our
  // internal token_expiry hasn't elapsed (Stytch invalidated it early).
  const isCvSessionError = isConnected && !isTokenExpired && (allCvError as any)?.status === 401;

  const disconnectMutation = useDisconnectCrowdVolt();
  const unlinkMutation = useUnlinkCrowdVoltListing();

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      showSuccess('CrowdVolt disconnected');
      trackEvent('crowdvolt_disconnected');
    } catch (err: any) {
      showError(err?.message || 'Failed to disconnect');
    }
  };

  const handleUnlink = async (ask_uqid: string) => {
    try {
      await unlinkMutation.mutateAsync(ask_uqid);
      showSuccess('Listing unlinked');
      trackEvent('cv_listing_unlinked', { askUqid: ask_uqid });
    } catch (err: any) {
      showError(err?.message || 'Failed to unlink');
    }
  };

  const handleToggleExpand = (ft_event_id: number) => {
    const expanding = expandedEventId !== ft_event_id;
    setExpandedEventId((prev) => (prev === ft_event_id ? null : ft_event_id));
    if (expanding) trackEvent('listing_event_expanded', { eventId: String(ft_event_id) });
  };

  const headerActions = (
    <>
      {isConnected && cvStatus && (
        <>
          <ConnectionChip
            name={cvStatus.crowdvolt_name || 'CrowdVolt'}
            contact={cvStatus.crowdvolt_phone || cvStatus.crowdvolt_email}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </>
      )}
      {/* The invitation to start, gated with the Connections banner. The
          expired-session reconnect below is NOT gated: that user is already
          connected, and stranding them would be a different thing entirely. */}
      {canSeeCvConnect && !isConnected && !isTokenExpired && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { setConnectCVOpen(true); trackEvent('connect_crowdvolt_modal_opened', { source: 'listings_header' }); }}
        >
          Connect CrowdVolt
        </Button>
      )}
    </>
  );

  const hasHeaderActions = Boolean(
    (isConnected && cvStatus) || (canSeeCvConnect && !isConnected && !isTokenExpired),
  );

  return (
    <PageShell width="wide">
      <PageHeader
        title="Listings"
        subtitle="Cross-platform view of your FT and CrowdVolt listings"
        actions={hasHeaderActions ? headerActions : undefined}
      />

      {/* Token expired banner */}
      {(isTokenExpired || isCvSessionError) && (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-white/70">CrowdVolt session expired — please reconnect</p>
          <div className="flex-shrink-0">
            <Button
              variant="primary"
              size="sm"
              onClick={() => { setConnectCVOpen(true); trackEvent('connect_crowdvolt_modal_opened', { source: 'reconnect_banner' }); }}
            >
              Reconnect
            </Button>
          </div>
        </div>
      )}

      {/* CrowdVolt connect banner */}
      {!isConnected && !isTokenExpired && (
        <div className="mb-4 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-white/[0.01] p-5 transition-all hover:border-violet-500/30">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-violet-500/20 bg-violet-500/10">
                <svg className="h-5 w-5 text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-white">Connect your CrowdVolt account</h3>
                <p className="mt-0.5 text-xs text-white/40">
                  Link CrowdVolt to see cross-platform listings, enable price mirroring, and manage everything from one dashboard.
                </p>
              </div>
            </div>
            <Link
              to={v2Path('/setup')}
              className={cx(
                'flex-shrink-0 rounded-lg bg-violet-500 px-4 py-2 text-center text-xs font-semibold text-white transition-all hover:bg-violet-400',
                FOCUS_RING,
              )}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}

      {/* Events list */}
      {eventsLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!eventsLoading && events.length === 0 && (
        <div className="space-y-4">
          <EmptyState
            icon={<TagIcon className="h-5 w-5" />}
            title={isConnected ? 'No linked event groups found yet' : 'No listings found'}
          />

          {isConnected && (
            <Panel
              title="CrowdVolt Active Listings"
              actions={
                <span className="text-sm text-white/40">
                  {allCvLoading ? 'Loading…' : `${allCvListings.length} found`}
                </span>
              }
            >
              {allCvLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full rounded-lg" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              )}

              {!allCvLoading && allCvListings.length === 0 && (
                <p className="text-sm text-white/40">No active CrowdVolt listings found.</p>
              )}

              {!allCvLoading && allCvListings.length > 0 && (
                <div className="space-y-2">
                  {allCvListings.map((cvl) => (
                    <CvSummaryRow
                      key={cvl.ask_uqid}
                      cvl={cvl}
                      onEditPrice={() => { setEditCVListing(cvl); setEditCVFTPrice(null); setEditCVOpen(true); }}
                      onLink={() => { setLinkCVListing(cvl); setLinkOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* Orphan CV listings — unmatched to any FT event */}
      {!eventsLoading && isConnected && orphanCvListings.length > 0 && (
        <Panel
          className="mb-4"
          title="CrowdVolt Listings"
          actions={<span className="text-sm text-white/40">{orphanCvListings.length} unmatched</span>}
        >
          <div className="space-y-1">
            {orphanCvListings.map((cvl) => (
              <CvSummaryRow
                key={cvl.ask_uqid}
                cvl={cvl}
                forceUnlinked
                onEditPrice={() => { setEditCVListing(cvl); setEditCVFTPrice(null); setEditCVOpen(true); }}
                onLink={() => { setLinkCVListing(cvl); setLinkOpen(true); }}
              />
            ))}
          </div>
        </Panel>
      )}

      {!eventsLoading && events.length > 0 && (
        <Panel
          title="Events"
          hint={`${events.length} event${events.length !== 1 ? 's' : ''}`}
          padding="none"
        >
          <div className="divide-y divide-white/[0.06]">
            {events.map((ev) => {
              const badge = eventLinkBadge(ev, isConnected);
              const isExpanded = expandedEventId === ev.ft_event_id;

              return (
                <div key={ev.ft_event_id} className="px-4 py-4 sm:px-5">
                  {/* Event row — clickable to expand */}
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    className={cx('w-full rounded-lg text-left', FOCUS_RING)}
                    onClick={() => handleToggleExpand(ev.ft_event_id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-white">{ev.ft_event_name}</span>
                          <Badge tone={badge.tone}>{badge.label}</Badge>
                        </div>
                        <p className="text-sm text-white/50">
                          {formatDate(ev.ft_event_date)}
                          {ev.ft_event_venue && ` · ${ev.ft_event_venue}`}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-xs text-white/50">
                            FT: <span className="text-white/80 tabular-nums">{ev.ft_active_listing_count}</span> active
                            {ev.ft_listing_count !== ev.ft_active_listing_count && (
                              <span className="text-white/30 tabular-nums"> / {ev.ft_listing_count} total</span>
                            )}
                          </span>
                          <span className="text-xs text-white/50">
                            CV:{' '}
                            <span className="text-white/80 tabular-nums">
                              {isConnected ? ev.cv_listing_count : '—'}
                            </span>
                          </span>
                          {isConnected && ev.linked_count > 0 && (
                            <span className="text-xs text-violet-400 tabular-nums">
                              {ev.linked_count} linked
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronDownIcon
                        className={cx(
                          'mt-1 h-4 w-4 flex-shrink-0 text-white/40 transition-transform',
                          isExpanded && 'rotate-180',
                        )}
                      />
                    </div>
                  </button>

                  {/* Inline expansion */}
                  {isExpanded && (
                    <EventDetail
                      event={ev}
                      cvConnected={isConnected}
                      allCvListings={allCvListings}
                      onEditFT={(l) => { setEditFTListing(l); setEditFTOpen(true); trackEvent('edit_ft_listing_opened', { listingId: String(l.id) }); }}
                      onEditCV={(cvl, ftPrice) => { setEditCVListing(cvl); setEditCVFTPrice(ftPrice); setEditCVOpen(true); trackEvent('edit_cv_listing_opened', { askUqid: cvl.ask_uqid }); }}
                      onLink={(cvl) => { setLinkCVListing(cvl); setLinkOpen(true); trackEvent('link_listing_modal_opened', { askUqid: cvl.ask_uqid }); }}
                      onUnlink={handleUnlink}
                      unlinkPending={unlinkMutation.isPending}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Modals */}
      <EditListingModal
        listing={editFTListing}
        open={editFTOpen}
        onClose={() => { setEditFTOpen(false); setEditFTListing(null); }}
      />
      <EditCVListingModal
        cvListing={editCVListing}
        ftPricePerTicket={editCVFTPrice}
        open={editCVOpen}
        onClose={() => { setEditCVOpen(false); setEditCVListing(null); setEditCVFTPrice(null); }}
      />
      <ConnectCrowdVoltModal
        open={connectCVOpen}
        onClose={() => setConnectCVOpen(false)}
      />
      <LinkListingModal
        cvListing={linkCVListing}
        open={linkOpen}
        onClose={() => { setLinkOpen(false); setLinkCVListing(null); }}
      />
    </PageShell>
  );
};
